import React, { useState } from 'react';
import { useAppLogs } from '../hooks/useAppLogs';
import { AppLogParams } from '../types/api';
import { debugLog } from '../utils/debug';

const AppLogsPanel: React.FC = () => {
  const { logs, loading, error, searchLogs, clearLogs } = useAppLogs();
  
  // Form state
  const [params, setParams] = useState<AppLogParams>({
    limit: 100,
    tail: true,
    type: 'app'
  });

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const quickFilters = [
    { label: 'Today', value: () => ({ date: new Date().toISOString().split('T')[0] }) },
    { label: 'Yesterday', value: () => ({ date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0] }) },
    { label: 'Errors Only', value: () => ({ level: 'error' as const }) },
    { label: 'Warnings Only', value: () => ({ level: 'warn' as const }) },
  ];

  const handleQuickFilter = (filterFunc: () => Partial<AppLogParams>) => {
    const filterParams = filterFunc();
    debugLog('AppLogsPanel', 'Applying quick filter', filterParams);
    const newParams = { 
      limit: 100, 
      tail: true, 
      type: 'app' as const,
      ...filterParams 
    };
    setParams(newParams);
  };

  const handleParamChange = (key: keyof AppLogParams, value: any) => {
    setParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = async () => {
    debugLog('AppLogsPanel', 'Searching app logs', params);
    setExpandedRows(new Set()); // Clear expanded rows on new search
    await searchLogs(params);
  };

  const clearAllFilters = () => {
    setParams({
      limit: 100,
      tail: true,
      type: 'app'
    });
    setExpandedRows(new Set());
    clearLogs();
  };

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getLogLevelColor = (level: string | null): string => {
    if (!level) return '#6c757d';
    switch (level.toLowerCase()) {
      case 'error': return '#dc3545';
      case 'warn': return '#ffc107';
      case 'info': return '#0dcaf0';
      case 'debug': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getLogLevelBackground = (level: string | null): string => {
    if (!level) return '#f8f9fa';
    switch (level.toLowerCase()) {
      case 'error': return '#f8d7da';
      case 'warn': return '#fff3cd';
      case 'info': return '#cff4fc';
      case 'debug': return '#f8f9fa';
      default: return '#f8f9fa';
    }
  };

  return (
    <div className="panel-content">
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Quick Filters */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Quick Filters</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          {quickFilters.map((filter) => (
            <button
              key={filter.label}
              onClick={() => handleQuickFilter(filter.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {filter.label}
            </button>
          ))}
          <button
            onClick={clearAllFilters}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Advanced Filters</h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px',
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: '#f8f9fa'
        }}>
          {/* Date Filter */}
          <div>
            <label htmlFor="app-logs-date" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Date:
            </label>
            <input
              id="app-logs-date"
              type="date"
              value={params.date || ''}
              onChange={(e) => handleParamChange('date', e.target.value)}
              style={{ 
                width: '100%', 
                padding: '8px', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            />
          </div>

          {/* Level Filter */}
          <div>
            <label htmlFor="app-logs-level" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Level:
            </label>
            <select
              id="app-logs-level"
              value={params.level || ''}
              onChange={(e) => handleParamChange('level', e.target.value || undefined)}
              style={{ 
                width: '100%', 
                padding: '8px', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">All Levels</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>
          </div>

          {/* Search Filter */}
          <div>
            <label htmlFor="app-logs-search" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search:
            </label>
            <input
              id="app-logs-search"
              type="text"
              value={params.search || ''}
              onChange={(e) => handleParamChange('search', e.target.value || undefined)}
              placeholder="Search in log messages..."
              style={{ 
                width: '100%', 
                padding: '8px', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            />
          </div>

          {/* Type Filter */}
          <div>
            <label htmlFor="app-logs-type" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Type:
            </label>
            <select
              id="app-logs-type"
              value={params.type || 'app'}
              onChange={(e) => handleParamChange('type', e.target.value as 'app' | 'error')}
              style={{ 
                width: '100%', 
                padding: '8px', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="app">App Logs</option>
              <option value="error">Error Logs</option>
            </select>
          </div>

          {/* Limit Filter */}
          <div>
            <label htmlFor="app-logs-limit" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Limit:
            </label>
            <select
              id="app-logs-limit"
              value={params.limit || 100}
              onChange={(e) => handleParamChange('limit', parseInt(e.target.value))}
              style={{ 
                width: '100%', 
                padding: '8px', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>

          {/* Order Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Order:
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="tail"
                  checked={params.tail === true}
                  onChange={() => handleParamChange('tail', true)}
                />
                Newest First
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="tail"
                  checked={params.tail === false}
                  onChange={() => handleParamChange('tail', false)}
                />
                Oldest First
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Search Controls */}
      <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px' }}>
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? (
            <>
              <span className="loading-spinner" style={{ marginRight: '8px' }} />
              Searching...
            </>
          ) : (
            'Search Logs'
          )}
        </button>
      </div>

      {/* Results */}
      {logs && (
        <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#ffffff', overflow: 'hidden' }}>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: 0 }}>App Log Results</h3>
            <p style={{ margin: '5px 0 0 0', color: '#6c757d' }}>
              Found {logs.count} entries {logs.data && `(showing ${logs.data.length})`}
              {logs.files && logs.files.length > 0 && (
                <span style={{ marginLeft: '10px' }}>
                  | Files: {logs.files.join(', ')}
                </span>
              )}
            </p>
          </div>

          {logs.data && logs.data.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      borderBottom: '1px solid #e9ecef',
                      width: '40px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e9ecef',
                      minWidth: '150px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      Timestamp
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e9ecef',
                      minWidth: '80px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      Level
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #e9ecef',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      Message
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.data.map((log, index) => {
                    const isExpanded = expandedRows.has(index);
                    return (
                      <React.Fragment key={index}>
                        <tr style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : getLogLevelBackground(log.level) }}>
                          <td style={{ 
                            padding: '8px', 
                            borderBottom: isExpanded ? 'none' : '1px solid #e9ecef',
                            textAlign: 'center',
                            width: '40px'
                          }}>
                            <button
                              onClick={() => toggleRowExpansion(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#6c757d',
                                padding: '2px 4px',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderBottom: isExpanded ? 'none' : '1px solid #e9ecef',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap'
                          }}>
                            {formatTimestamp(log.timestamp)}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderBottom: isExpanded ? 'none' : '1px solid #e9ecef',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: getLogLevelColor(log.level),
                            textTransform: 'uppercase'
                          }}>
                            {log.level || 'UNKNOWN'}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderBottom: isExpanded ? 'none' : '1px solid #e9ecef',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            wordBreak: 'break-word',
                            maxWidth: '600px'
                          }}>
                            <div>
                              {log.message}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#f1f3f4' }}>
                            <td colSpan={4} style={{ 
                              padding: '15px', 
                              borderBottom: '1px solid #e9ecef',
                              backgroundColor: '#f8f9fa'
                            }}>
                              <div style={{ marginBottom: '8px' }}>
                                <strong style={{ fontSize: '12px', color: '#495057' }}>Raw Log Entry:</strong>
                              </div>
                              <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '12px',
                                fontFamily: 'monospace',
                                fontSize: '11px',
                                lineHeight: '1.4',
                                color: '#212529',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                maxHeight: '200px',
                                overflowY: 'auto'
                              }}>
                                {log.raw}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
              No app logs found matching your search criteria
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppLogsPanel;